import React, { useEffect } from "react";
import Pica from "pica";
import {
  getBboxSizeFromUnrotated as coreGetBboxSizeFromUnrotated,
  getEdgedFrameValueFromFlexibleFrame as coreGetEdgedFrameValueFromFlexibleFrame,
  getFlexibleEdgedFrameValue as coreGetFlexibleEdgedFrameValue,
  getUnrotatedSizeFromBbox as coreGetUnrotatedSizeFromBbox,
  getBboxSizeFromUnrotated,
  type FlexibleFrameDecodeInput,
  type FlexibleFrameRatios,
  type WatermarkRect,
} from "../core";
import {
  DEFAULT_TEXT_BG_PADDING,
  getTextBackgroundInsets,
} from "./fontsConstants";
import uploadedFontManager from "../Utils/uploadedFontManager";
import { fontList } from "./config";

interface Point2D {
  x: number;
  y: number;
}

/**
 * EWW-464: iOS's font resolver does an exact-filename lookup against its OWN app bundle, and
 * for these fonts iOS's actual bundled file does not match the clean display name in `fontList`
 * (config.ts) -- case, spaces, underscores, or a hyphen differ, e.g. the font shown to the user
 * as "Endor" is bundled on iOS as `ENDOR___.ttf`. Deliberately keyed/valued by iOS specifically,
 * not by any shared/Android convention -- Android's own bundle already matches the clean names,
 * so this map exists solely to make WEB's saved payload match iOS's files; Android resolving its
 * own templates is unaffected either way.
 */
const iosCustomFontFile: Record<string, string> = {
  ArmyWide: "Army_Wide.ttf",
  BurnstownDam: "burnstown dam.ttf",
  Endor: "ENDOR___.ttf",
  EndorAlt: "ENDORALT.ttf",
  Evanescent: "evanescent.ttf",
  EvanescentP: "evanescent_p.ttf",
  ItLivesInTheSwampBRK: "ilits.ttf",
  LavanderiaDelicate: "Lavanderia Delicate.otf",
  LavanderiaRegular: "Lavanderia Regular.otf",
  Musicals: "musicals.ttf",
  PentaGramsMalefissent: "PentaGrams Malefissent.ttf",
  RochesterRegular: "Rochester-Regular.ttf",
  SmartieCAPS: "SMARC___.TTF",
  TheEndFont: "TheEndFont(RF).ttf",
  YoungBeautiful: "Young & Beautiful.ttf",
};

/**
 * EWW-464: iOS's SAVE side writes `UIFont.fontName` -- the font's own internal PostScript name,
 * read from the file's metadata -- not the clean display name and not the bundle filename
 * either. A template created on iOS therefore arrives with THIS in its "font" field, which is
 * why fixing only the filename side (`iosCustomFontFile` above) was not enough: that only
 * covers WEB-created templates being opened on iOS, not the reverse.
 *
 * Extracted by reading each font file's own `name` table (nameID 6) directly -- most of these
 * 30 fonts' PostScript name already equals the clean display name (so `normalizeTemplateFontForRender`'s
 * existing fallback already handles them); only these 8 actually differ and need mapping back.
 */
const iosPostScriptFontName: Record<string, string> = {
  "BurnstownDam-Regular": "BurnstownDam",
  "Lavanderia-Delicate": "LavanderiaDelicate",
  "Lavanderia-Regular": "LavanderiaRegular",
  "Lavanderia-Sturdy": "Lavanderia Sturdy",
  "PentaGrams-Malefissent-Regular": "PentaGramsMalefissent",
  "Rochester-Regular": "RochesterRegular",
  "TheEnd-REG": "TheEndFont",
  "Young&Beautiful": "YoungBeautiful",
};

const helper_functions = {

  normalizeTemplateFontForRender(fontName: unknown, fontId?: unknown): string {
    const fId = typeof fontId === "string" ? fontId.trim() : "";
    if (fId) {
      const customFont = uploadedFontManager.findFontById(fId);
      if (customFont) return customFont.postScriptName;
    }

    const raw = String(fontName ?? "").trim();
    if (!raw) return "Sans-serif";

    // A real uploaded custom font must never pass through the legacy iOS/Android
    // name-remapping rules below -- those rules only exist for the known bundled/system
    // font names and cannot distinguish a legitimate collision from the quirk they fix.
    const customFont = uploadedFontManager.findFontByPostScriptName(raw);
    if (customFont) {
      return customFont.postScriptName;
    }

    // CSS font-family names in web are registered without file extensions.
    const cleaned = raw.replace(/\.(ttf|otf|woff2?|eot)$/i, "");
    // EWW-464: reverse of `iosCustomFontFile` below -- a template saved with iOS's actual raw
    // filename (case/spacing/etc intact) must still resolve back to the clean family name web's
    // own `fonts.css` registers, or reopening that template on web shows the fallback font.
    const iosMatch = Object.entries(iosCustomFontFile).find(
      ([, file]) => file.replace(/\.(ttf|otf)$/i, "").toLowerCase() === cleaned.toLowerCase()
    );
    if (iosMatch) return iosMatch[0];
    // EWW-464: an iOS-CREATED template's "font" value -- see `iosPostScriptFontName`'s own doc.
    if (iosPostScriptFontName[cleaned]) return iosPostScriptFontName[cleaned];
    // Backwards compatibility for old font names with special characters
    if (cleaned === "TheEndFont(RF)") return "TheEndFont";
    if (cleaned === "Young&Beautiful") return "YoungBeautiful";
    // EWW-451: Android's "simple"/default text style saves the platform's own default
    // typeface name (Roboto) instead of leaving font empty like web/iOS do. Left as-is, web
    // resolves "Roboto" to an actual Roboto webfont -- a visibly different look than web's own
    // default -- so treat it the same as "no font specified" here too.
    if (/^roboto(-regular)?$/i.test(cleaned)) return "Sans-serif";

    // Check if cleaned matches any standard or bundled font in fontList
    const standardMatch = fontList.find((f) => f.toLowerCase() === cleaned.toLowerCase());
    if (standardMatch) return standardMatch;

    // If font is not standard, not registered in uploadedFontManager, and not found,
    // it was a custom font that was deleted from the upload list.
    // Reset to the default font applied to text: "Arial".
    return "Arial";
  },
  normalizeTemplateFontForSave(fontName: unknown, fontId?: unknown): string {
    const fId = typeof fontId === "string" ? fontId.trim() : "";
    if (fId) {
      const customFont = uploadedFontManager.findFontById(fId);
      if (customFont) return customFont.postScriptName;
    }

    let raw = String(fontName ?? "").trim();
    if (!raw) return "Sans-serif";

    // Custom uploaded fonts should be preserved as their PostScript name and not rewritten
    const customFont = uploadedFontManager.findFontByPostScriptName(raw);
    if (customFont) {
      return customFont.postScriptName;
    }

    // If extension already exists, keep as-is (mobile payload compatibility).
    if (/\.(ttf|otf|woff2?|eot)$/i.test(raw)) return raw;

    // Handle backwards compatibility for old font names
    if (raw === "TheEndFont(RF)") raw = "TheEndFont";
    if (raw === "Young&Beautiful") raw = "YoungBeautiful";

    // EWW-464: for the 15 fonts where iOS's bundle filename doesn't match the clean display
    // name, save iOS's ACTUAL filename verbatim (case/spacing/hyphen intact) so its
    // exact-filename lookup finds the real file. Deliberately does not affect Android -- its
    // bundle already matches the clean names below, and swapping to iOS's names here has no
    // bearing on Android's own resolution.
    if (iosCustomFontFile[raw]) return iosCustomFontFile[raw];

    // Mobile custom watermark fonts may have mixed extensions.
    // Use explicit mapping from bundled font assets; unknown fonts remain unchanged.
    const mobileCustomFontExt: Record<string, string> = {
      ArmyWide: "ttf",
      BurnstownDam: "ttf",
      Endor: "ttf",
      EndorAlt: "ttf",
      Evanescent: "ttf",
      EvanescentP: "ttf",
      ItLivesInTheSwampBRK: "ttf",
      JennaSue: "ttf",
      "Lavanderia Sturdy": "otf",
      LavanderiaDelicate: "otf",
      LavanderiaRegular: "otf",
      LittleLordFontleroyNF: "ttf",
      Maranallo: "ttf",
      MaranalloHigh: "ttf",
      MaranalloHighItalic: "ttf",
      MaranalloItalic: "ttf",
      MetalMacabre: "ttf",
      Musicals: "ttf",
      Nightbird: "ttf",
      OdalisqueNF: "ttf",
      PentaGramsMalefissent: "ttf",
      RochesterRegular: "ttf",
      Rubber: "ttf",
      SeasideResortNF: "ttf",
      SmartieCAPS: "ttf",
      TeamSpiritNF: "ttf",
      TheEndFont: "ttf",
      Trocadero: "ttf",
      WereWolf: "ttf",
      YoungBeautiful: "ttf",
    };
    const ext = mobileCustomFontExt[raw];
    // EWW-464: only fonts we KNOW are real bundled files (the map above) get an extension.
    // Everything else is a plain OS-native system font (Arial, Georgia, ...) with no real file
    // behind it at all -- guessing ".ttf" for those was simply wrong, not a harmless default.
    return ext ? `${raw}.${ext}` : raw;
  },
  pixel_To_percent(x: number, y: number, CW: number, CH: number): Point2D {
    const Px = (x / CW) * 100;
    const Py = (y / CH) * 100;
    return {
      x: Px,
      y: Py,
    };
  },
  percent_To_pixel(x: number, y: number, CW: number, CH: number): Point2D {
    const Px = (x / 100) * CW;
    const Py = (y / 100) * CH;
    return {
      x: Px,
      y: Py,
    };
  },
  useDebounceEffect(fn: (...args: unknown[]) => void, waitTime: number, deps: unknown[]): void {
    useEffect(() => {
      const t = setTimeout(() => {
        fn(...deps);
      }, waitTime);

      return () => {
        clearTimeout(t);
      };
    }, deps);
  },
  degToRad(angle: number): number {
    return (angle / 180) * Math.PI;
  },
  // Rotates point (Px, Py) around (centerx, centery) by `degree` radians.
  newPoints(centerx: number, centery: number, Px: number, Py: number, degree: number): { centerx: number; centery: number; oldx: number; oldy: number; X: number; Y: number } {
    const x =
      centerx +
      (Px - centerx) * Math.cos(degree) -
      (Py - centery) * Math.sin(degree);
    const y =
      centery +
      (Px - centerx) * Math.sin(degree) +
      (Py - centery) * Math.cos(degree);
    return {
      centerx: centerx,
      centery: centery,
      oldx: Px,
      oldy: Py,
      X: x,
      Y: y,
    };
  },
  // Moves a watermark to an edge/corner/center of the image (align buttons), accounting for
  // rotation so it still lines up correctly even when tilted.
  alignment(
    property: number,
    imageProperties: {
      alignEnable?: string;
      deg?: number;
      image?: { width: number; height: number };
      scaleX?: number;
      scaleY?: number;
      [key: string]: unknown;
    },
    shapeRef: React.RefObject<{ width: () => number; height: () => number } | null>,
    degToRad: (angle: number) => number,
    getCenterPoint: () => Point2D,
    yCordsOfMainImage: number,
    xCordsOfMainImage: number,
    scaleFactorDifference: number,
    canvasImageSize: { width: number; height: number },
    setYCords: (v: number) => void,
    setXCords: (v: number) => void,
    newPoints: (cx: number, cy: number, px: number, py: number, deg: number) => { X: number; Y: number }
  ): void {
    if (imageProperties.hasOwnProperty("alignEnable") && shapeRef.current) {
      const degree = Number(imageProperties.deg) || 0;
      const deg = degToRad(degree);
      let xOld = 0, yOld = 0, centerx = 0, centery = 0, width = 0, height = 0;
      // For text (property = 1), use actual text dimensions without scaleFactorDifference
      // For images (property = 0), use image dimensions with scale
      width = property
        ? shapeRef.current.width()
        : imageProperties.image.width * imageProperties.scaleX;
      height = property
        ? shapeRef.current.height()
        : imageProperties.image.height * imageProperties.scaleY;
      centerx = xCordsOfMainImage + width / 2;
      centery = yCordsOfMainImage + height / 2;

      switch (imageProperties.alignEnable) {
        case "TopLeft":
          xOld = xCordsOfMainImage;
          yOld = yCordsOfMainImage;
          break;
        case "TopRight":
          xOld = xCordsOfMainImage + canvasImageSize.width - width;
          yOld = yCordsOfMainImage;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "BottomLeft":
          xOld = xCordsOfMainImage;
          yOld = yCordsOfMainImage + canvasImageSize.height - height;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "BottomRight":
          xOld = xCordsOfMainImage + canvasImageSize.width - width;
          yOld = yCordsOfMainImage + canvasImageSize.height - height;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "TopCenter":
          xOld = xCordsOfMainImage + canvasImageSize.width / 2 - width / 2;
          yOld = yCordsOfMainImage;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "BottomCenter":
          xOld = xCordsOfMainImage + canvasImageSize.width / 2 - width / 2;
          yOld = yCordsOfMainImage + canvasImageSize.height - height;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        case "Center":
          xOld = xCordsOfMainImage + canvasImageSize.width / 2 - width / 2;
          yOld = yCordsOfMainImage + canvasImageSize.height / 2 - height / 2;
          centerx = xOld + width / 2;
          centery = yOld + height / 2;
          break;
        default:
          break;
      }

      // The box's 4 corners, rotated -- needed to find the actual top/right/bottom/left edge
      // after rotation (a rotated box's edges are no longer just x/y/x+width/y+height).
      const yOldPL = yOld + height;
      const xOldPR = xOld + width;
      const xOldPB = xOld + width;
      const yOldPB = yOld + height;
      const newPT = newPoints(centerx, centery, xOld, yOld, deg);
      const newPR = newPoints(centerx, centery, xOldPR, yOld, deg);
      const newPB = newPoints(centerx, centery, xOldPB, yOldPB, deg);
      const newPL = newPoints(centerx, centery, xOld, yOldPL, deg);

      let newX = 0;
      let newY = 0;

      // For images (property = 0), positions are center (with offsetX/offsetY)
      const centerOffsetX = property ? 0 : getCenterPoint().x * imageProperties.scaleX;
      const centerOffsetY = property ? 0 : getCenterPoint().y * imageProperties.scaleY;

      switch (imageProperties.alignEnable) {
        case "TopLeft":
          newX =
            Math.min(newPL.X, newPT.X, newPR.X, newPB.X) - xCordsOfMainImage;
          newY =
            Math.min(newPL.Y, newPT.Y, newPR.Y, newPB.Y) - yCordsOfMainImage;
          setXCords(
            -1 * newX +
              xCordsOfMainImage +
              centerOffsetX
          );
          setYCords(
            -1 * newY +
              yCordsOfMainImage +
              centerOffsetY
          );
          break;
        case "TopRight":
          newX =
            Math.max(newPL.X, newPT.X, newPR.X, newPB.X) -
            canvasImageSize.width -
            xCordsOfMainImage;
          newY =
            Math.min(newPL.Y, newPT.Y, newPR.Y, newPB.Y) - yCordsOfMainImage;
          setXCords(
            -1 * newX + xOld + centerOffsetX
          );
          setYCords(
            -1 * newY +
              yCordsOfMainImage +
              centerOffsetY
          );
          break;

        case "BottomLeft":
          newX =
            Math.min(newPL.X, newPT.X, newPR.X, newPB.X) - xCordsOfMainImage;
          newY =
            Math.max(newPL.Y, newPT.Y, newPR.Y, newPB.Y) -
            canvasImageSize.height -
            yCordsOfMainImage;
          setXCords(
            -1 * newX +
              xCordsOfMainImage +
              centerOffsetX
          );
          setYCords(
            -1 * newY + yOld + centerOffsetY
          );
          break;

        case "BottomRight":
          newX =
            Math.max(newPL.X, newPT.X, newPR.X, newPB.X) -
            canvasImageSize.width -
            xCordsOfMainImage;
          newY =
            Math.max(newPL.Y, newPT.Y, newPR.Y, newPB.Y) -
            canvasImageSize.height -
            yCordsOfMainImage;
          setXCords(
            -1 * newX + xOld + centerOffsetX
          );
          setYCords(
            -1 * newY + yOld + centerOffsetY
          );
          break;
        case "TopCenter":
          newY =
            Math.min(newPL.Y, newPT.Y, newPR.Y, newPB.Y) - yCordsOfMainImage;
          setXCords(
            xOld +
              centerOffsetX
          );
          setYCords(
            -1 * newY + yOld + centerOffsetY
          );
          break;
        case "BottomCenter":
          newY =
            Math.max(newPL.Y, newPT.Y, newPR.Y, newPB.Y) -
            canvasImageSize.height -
            yCordsOfMainImage;
          setXCords(
            xOld +
              centerOffsetX
          );
          setYCords(
            -1 * newY + yOld + centerOffsetY
          );
          break;
        case "Center":
          setXCords(
            canvasImageSize.width / 2 +
              xCordsOfMainImage -
              width / 2 +
              centerOffsetX
          );
          setYCords(
            canvasImageSize.height / 2 +
              yCordsOfMainImage -
              height / 2 +
              centerOffsetY
          );
          break;
        default:
          break;
      }
    }
  },
  dataURLtoFile(dataurl: string, filename: string): File | undefined {
    // dataurl like data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...
    if (dataurl) {
      const arr = dataurl.split(",");
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], filename);
    }
  },
  blobToFile(blob: Blob, fileName: string): File {
    // binary data
    const file = new File([blob], fileName, { type: blob.type });
    return file;
  },
  convertToDataURL(file: File | string | null | undefined): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      // Handle case when file is not provided or is already a data URL string
      if (!file) {
        reject(new Error("No file provided"));
        return;
      }

      // If it's already a data URL or regular URL string, resolve with it
      if (typeof file === "string") {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  },
  blobToDataURL(blob: Blob): void {
    const reader = new FileReader();
    reader.onloadend = function () {
      return reader.result;
    };
    reader.readAsDataURL(blob);
  },
  WebToFile(url: string): Promise<File> {
    return fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const filename = url.substring(url.lastIndexOf("/") + 1);
        const imageFile = new File([blob], filename, { type: blob.type });
        return imageFile;
      });
  },
  isSvgFile(file: File | null | undefined): boolean {
    try {
      if (!file) return false;
      const type = String(file.type || "").toLowerCase();
      const name = String(file.name || "").toLowerCase();
      return type.includes("svg") || name.endsWith(".svg");
    } catch {
      return false;
    }
  },
  async convertSvgFileToHighResPng(file: File, scale = 4): Promise<File> {
    const svgText = await file.text();
    return await this.convertSvgStringToHighResPng(svgText, file?.name, scale);
  },
  async convertSvgStringToHighResPng(svgString: string, name?: string, scale = 4): Promise<File> {
    const safeScale = Math.max(1, Number(scale) || 1);

    const filename = (() => {
      const raw = String(name || "watermark.svg");
      if (raw.toLowerCase().endsWith(".png")) return raw;
      if (raw.toLowerCase().endsWith(".svg")) return raw.slice(0, -4) + ".png";
      return raw + ".png";
    })();

    return await new Promise((resolve, reject) => {
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";

        const blob = new Blob([svgString], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);

        img.onload = () => {
          try {
            const baseW = img.naturalWidth || img.width || 300;
            const baseH = img.naturalHeight || img.height || 150;

            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(baseW * safeScale));
            canvas.height = Math.max(1, Math.round(baseH * safeScale));

            const ctx = canvas.getContext("2d");
            if (!ctx) {
              URL.revokeObjectURL(url);
              reject(new Error("Canvas context failed"));
              return;
            }

            // Keep background transparent; enable high-quality resampling if supported.
            ctx.imageSmoothingEnabled = true;
            try {
              ctx.imageSmoothingQuality = "high";
            } catch {
              // ignore
            }

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(
              (pngBlob) => {
                URL.revokeObjectURL(url);
                if (!pngBlob) {
                  reject(new Error("Blob conversion failed"));
                  return;
                }
                resolve(new File([pngBlob], filename, { type: "image/png" }));
              },
              "image/png",
              1
            );
          } catch (e) {
            URL.revokeObjectURL(url);
            reject(e);
          }
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("Failed to load SVG"));
        };

        img.src = url;
      } catch (e) {
        reject(e);
      }
    });
  },
  isImageExtension(filename: string): string | false {
    const imageExtensions = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "tiff",
      "svg",
      "webp",
      "ico",
      "jfif",
      "heic",
      "heif",
    ];
    const ext = filename.split(".").pop().toLowerCase();
    return imageExtensions.includes(ext) ? ext : false;
  },
  decryptToScaleAndSize(widthPercentage: number, canvasWidth: number, originalSize: number): number {
    // calculate image width and scale according to canvas
    const imageWidthOnCanvas = widthPercentage * canvasWidth;
    const scale = imageWidthOnCanvas / originalSize;
    return scale;
  },
  encryptToWidthPercentage(
    scaleX: number,
    scaleY: number,
    imagewidth: number,
    imageHeight: number,
    canvasWidth: number,
    xPoint: number,
    yPoint: number
  ): { imageWidthOnCanvas: number; imageheightOnCanvas: number; x: number; y: number } {
    // Normalizing values according to canvas
    const imageWidthOnCanvas = (imagewidth * scaleX) / canvasWidth;
    const imageheightOnCanvas = (imageHeight * scaleY) / canvasWidth;
    const newCordsPx = helper_functions.percent_To_pixel(
      xPoint,
      yPoint,
      canvasWidth,
      canvasWidth
    );

    const x = newCordsPx.x - (imagewidth * scaleX) / 2;
    const y = newCordsPx.y - (imageHeight * scaleY) / 2;
    const newCordsPc = helper_functions.pixel_To_percent(
      x,
      y,
      canvasWidth,
      canvasWidth
    );

    return {
      imageWidthOnCanvas,
      imageheightOnCanvas,
      x: newCordsPc.x,
      y: newCordsPc.y,
    };
  },
  resetOffset(x: number, y: number, width: number, height: number): Point2D {
    // for re-centering / re-positioning
    const newX = x + width / 2;
    const newY = y + (height * width) / 2;

    return {
      x: newX,
      y: newY,
    };
  },
  /**
   * Encode flexible_frame for template save — matches Android `getFlexibleFrameValue`:
   * 3×3 grid, ratios relative to the cell containing the watermark center, scale vs min(regionW, regionH),
   * region = row*3+col (0…8), plus center ratios vs full image.
   * @param watermarkFrame - {x, y, width, height} = axis-aligned bbox relative to image (top-left origin)
   * @param imageFrame - {width, height} = full image size
   */
  // Equivalent Functions, Create (implementation: src/core)
  getFlexibleEdgedFrameValue(
    watermarkFrame: WatermarkRect,
    imageFrame: { width: number; height: number }
  ): FlexibleFrameRatios {
    return coreGetFlexibleEdgedFrameValue(watermarkFrame, imageFrame);
  },
  /**
   * Forward bbox projection: axis-aligned bbox size for a rotated rectangle.
   * Matches admin's `getBboxSizeFromUnrotated` (uses |cos|/|sin| symmetry).
   */
  getBboxSizeFromUnrotated(width: number, height: number, rotationDeg: number): { width: number; height: number } {
    return coreGetBboxSizeFromUnrotated(width, height, rotationDeg);
  },
  /**
   * Inverse bbox math: recover an unrotated (w0,h0) from a rotated bbox (W,H).
   * At 45°+k·90° the linear system is singular; pass `unrotatedAspectWOverH` (w/h) when available
   * (admin payloads: `data.height` is ios_height = h/w → w/h = 1/data.height).
   */
  getUnrotatedSizeFromBbox(
    bboxWidth: number,
    bboxHeight: number,
    rotationDeg: number,
    unrotatedAspectWOverH?: number
  ): { width: number; height: number } {
    return coreGetUnrotatedSizeFromBbox(bboxWidth, bboxHeight, rotationDeg, unrotatedAspectWOverH);
  },
  /**
   * Calculate watermark frame position from flexible_frame data
   * This matches the mobile app's getEgdedFrameValueFromFlexibleFrame logic
   * @param {Object} flexibleFrame - Flexible frame data with ratios
   * @param {Object} imageSize - Image dimensions {width, height}
   * @returns {Object} - Position and size {x, y, width, height} relative to image
   */
  // Equivalent Functions, Apply (implementation: src/core)
  getEdgedFrameValueFromFlexibleFrame(
    flexibleFrame: FlexibleFrameDecodeInput,
    imageSize: { width: number; height: number }
  ): (WatermarkRect & { contentWidth?: number; contentHeight?: number }) | null {
    return coreGetEdgedFrameValueFromFlexibleFrame(flexibleFrame, imageSize);
  },
  dataWrapperEncrypt(data: Record<string, any>, tag: number, canvasSize: number, imageSize?: { width: number; height: number; offsetX?: number; offsetY?: number } | null): Record<string, any> {
    // Create
    const size = this.encryptToWidthPercentage(
      data.scaleX,
      data.scaleY,
      data.containerWidth,
      data.containerHight,
      canvasSize,
      data.x,
      data.y
    );

    let dataReturn: Record<string, any> = {
      type: data.type, /// image , text
      id: data.id, /// local id for watermark
      tag: tag, /// z axis value
      // ?? not || — 0° is valid. Items may still carry stale `rotation` from API after `deg` was set to 0.
      rotation: Number(data.deg ?? data.rotation ?? 0) || 0, /// degrees
      width: size.imageWidthOnCanvas, /// ratio value from 1 to 0 accoridng to canvas
      height: size.imageheightOnCanvas / size.imageWidthOnCanvas, /// ratio value from 1 to 0 accoridng to width
      opacity: data.opacity,
    };

    if (data.type === "image" || data.type === "signature") {
      // Calculate watermark position relative to image for flexible_frame
      let flexible_frame = null;

      // Calculate flexible_frame if image dimensions provided, or use canvas as reference
      const imageWidth = imageSize?.width || canvasSize;
      const imageHeight = imageSize?.height || canvasSize;
      const imageOffsetX = imageSize?.offsetX || 0;
      const imageOffsetY = imageSize?.offsetY || 0;

      if (imageWidth > 0 && imageHeight > 0) {
        // Convert canvas-relative position to image-relative position
        // data.x and data.y are percentages of canvas (center-based).
      // If alignEnable is set (and flexible_frame is absent), recompute the center
      // from alignment geometry instead of the stale Redux x/y — alignment runs only
      // in the LogoImage effect and never writes back to Redux.
      let canvasX = (data.x / 100) * canvasSize;
      let canvasY = (data.y / 100) * canvasSize;

      const alignEnable = String(data.alignEnable ?? "");
      if (alignEnable && alignEnable !== "None" && !data.flexible_frame) {
        const rot = Number(data.deg ?? data.rotation ?? 0) || 0;
        const unrotW2 =
          (Number(data.containerWidth) > 0
            ? Number(data.containerWidth)
            : size.imageWidthOnCanvas * canvasSize) *
          (Number(data.scaleX) || 1);
        const unrotH2 =
          (Number(data.containerHight) > 0
            ? Number(data.containerHight)
            : (size.imageheightOnCanvas /
                Math.max(0.0001, size.imageWidthOnCanvas)) *
              (size.imageWidthOnCanvas * canvasSize)) *
          (Number(data.scaleY) || 1);
        const bbox2 = getBboxSizeFromUnrotated(unrotW2, unrotH2, rot);

        const imageOffX = imageSize?.offsetX || 0;
        const imageOffY = imageSize?.offsetY || 0;
        const imgW = imageSize?.width || canvasSize;
        const imgH = imageSize?.height || canvasSize;

        let topLeftX = imageOffX;
        let topLeftY = imageOffY;

        switch (alignEnable) {
          case "TopLeft":
            topLeftX = imageOffX;
            topLeftY = imageOffY;
            break;
          case "TopRight":
            topLeftX = imageOffX + imgW - bbox2.width;
            topLeftY = imageOffY;
            break;
          case "BottomLeft":
            topLeftX = imageOffX;
            topLeftY = imageOffY + imgH - bbox2.height;
            break;
          case "BottomRight":
            topLeftX = imageOffX + imgW - bbox2.width;
            topLeftY = imageOffY + imgH - bbox2.height;
            break;
          case "Center":
            topLeftX = imageOffX + (imgW - bbox2.width) / 2;
            topLeftY = imageOffY + (imgH - bbox2.height) / 2;
            break;
          case "TopCenter":
            topLeftX = imageOffX + (imgW - bbox2.width) / 2;
            topLeftY = imageOffY;
            break;
          case "BottomCenter":
            topLeftX = imageOffX + (imgW - bbox2.width) / 2;
            topLeftY = imageOffY + imgH - bbox2.height;
            break;
          case "MidLeft":
            topLeftX = imageOffX;
            topLeftY = imageOffY + (imgH - bbox2.height) / 2;
            break;
          case "MidRight":
            topLeftX = imageOffX + imgW - bbox2.width;
            topLeftY = imageOffY + (imgH - bbox2.height) / 2;
            break;
        }

        canvasX = topLeftX + bbox2.width / 2;
        canvasY = topLeftY + bbox2.height / 2;
      }

        // Compute unrotated render size (px) for this watermark on the canvas.
        const unrotW =
          (Number(data.containerWidth) > 0 ? Number(data.containerWidth) : (size.imageWidthOnCanvas * canvasSize)) *
          (Number(data.scaleX) || 1);
        const unrotH =
          (Number(data.containerHight) > 0
            ? Number(data.containerHight)
            : (size.imageheightOnCanvas / Math.max(0.0001, size.imageWidthOnCanvas)) * (size.imageWidthOnCanvas * canvasSize)) *
          (Number(data.scaleY) || 1);

        // Admin-aligned: flexible_frame should describe the ROTATED bbox (axis-aligned).
        const rot = Number(data.deg ?? data.rotation ?? 0) || 0;
        const bbox = this.getBboxSizeFromUnrotated(unrotW, unrotH, rot);
        const watermarkTopLeftX = canvasX - bbox.width / 2;
        const watermarkTopLeftY = canvasY - bbox.height / 2;

        // Calculate watermark position relative to image
        const relativeX = watermarkTopLeftX - imageOffsetX;
        const relativeY = watermarkTopLeftY - imageOffsetY;

        // Create watermark frame relative to image
        const watermarkFrame = {
          x: relativeX,
          y: relativeY,
          width: bbox.width,
          height: bbox.height,
        };

        // Calculate flexible_frame
        flexible_frame = this.getFlexibleEdgedFrameValue(watermarkFrame, {
          width: imageWidth,
          height: imageHeight,
        });

        // EWW-437: Stable-Axis Responsive Scaling -- additive fields alongside the legacy
        // scale_ratio/aspect_ratio above (which derive size from min(imageWidth, imageHeight),
        // the root cause of watermarks resizing wrong when template aspect ratio changes).
        // `bbox`/`unrotW`/`unrotH` are already computed above for the legacy fields; reused here,
        // not recomputed.
        //
        // Content and bounding ratios are each proportions of their OWN matching canvas
        // dimension (width vs canvas width, height vs canvas height) -- decode uses whichever
        // one `size_axis` selects and derives the other from aspect_ratio/content_aspect_ratio,
        // so only the driving axis's stored ratio is ever actually read back. EWW-463: matches
        // mobile's `FlexibleFrameUtils.getFlexibleEdgedFrameValue`, which normalizes each ratio
        // against its own axis the same way.
        flexible_frame = {
          ...flexible_frame,
          bounding_width_ratio: bbox.width / imageWidth,
          bounding_height_ratio: bbox.height / imageHeight,
          content_width_ratio: unrotW / imageWidth,
          content_height_ratio: unrotH / imageHeight,
          content_aspect_ratio: unrotW / Math.max(0.0001, unrotH),
          size_axis: unrotW >= unrotH ? (0 as const) : (1 as const),
        };
      }

      // Admin-aligned: width = ios_width (unrotated width/ref), height = ios_height (unrotated aspect).
      // This preserves watermark scale/aspect regardless of rotation (matches admin useSaveTemplate).
      const ref = Math.min(imageWidth, imageHeight) || 1;
      const unrotW =
        (Number(data.containerWidth) > 0 ? Number(data.containerWidth) : (size.imageWidthOnCanvas * canvasSize)) *
        (Number(data.scaleX) || 1);
      const unrotH =
        (Number(data.containerHight) > 0
          ? Number(data.containerHight)
          : (size.imageheightOnCanvas / Math.max(0.0001, size.imageWidthOnCanvas)) * (size.imageWidthOnCanvas * canvasSize)) *
        (Number(data.scaleY) || 1);
      const ios_width = unrotW / ref;
      const ios_height = unrotH / Math.max(0.0001, unrotW);

      // For flexible templates, store x/y as center ratios (matches Android `center_x_ratio` / `center_y_ratio` in flexible_frame).
      const adminX =
        flexible_frame && typeof flexible_frame.center_x_ratio === "number"
          ? flexible_frame.center_x_ratio
          : null;
      const adminY =
        flexible_frame && typeof flexible_frame.center_y_ratio === "number"
          ? flexible_frame.center_y_ratio
          : null;

      const formatDateDdMmYyyy = () => {
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = String(d.getFullYear());
        return `${dd}-${mm}-${yyyy}`;
      };

      dataReturn = {
        ...dataReturn,
        date: formatDateDdMmYyyy(),
        name: data.name, /// name format wm_image_unixtimestamp
        originalSize: data.size, /// watermark size for storage in bytes
        sub_type: data.property || data.sub_type || "file", /// match admin
        // Default legacy fallback (non-flexible): keep existing encoding
        x: adminX !== null ? adminX : size.x / 100,
        y: adminY !== null ? adminY : size.y / 100,
        // Admin: width = ios_width, height = ios_height (not AABB content box)
        width: ios_width,
        height: ios_height,
        // Add flexible_frame if calculated (admin deletes ios_* from frame; we never add them)
        ...(flexible_frame && { flexible_frame: flexible_frame }),
      };
    }
    if (data.type === "text") {
      const imageWidth = imageSize?.width || canvasSize;
      const imageHeight = imageSize?.height || canvasSize;
      const imageOffsetX = imageSize?.offsetX || 0;
      const imageOffsetY = imageSize?.offsetY || 0;

      const formatDateDdMmYyyy = () => {
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = String(d.getFullYear());
        return `${dd}-${mm}-${yyyy}`;
      };

      // Text x/y in editor are TOP-LEFT percent coords of the unrotated text box.
      const topLeftPx = this.percent_To_pixel(
      Number(data.x) || 0,
      Number(data.y) || 0,
      canvasSize,
      canvasSize
    );

      const _bgColor = String(data.textBackgroundColor ?? "").trim();
      const _hasBg = _bgColor.length > 0 && _bgColor !== "#00000000";
      const _insets = _hasBg
        ? getTextBackgroundInsets({
            normalizedFont: this.normalizeTemplateFontForRender(data.font ?? "", data.font_id),
            bgPadding: DEFAULT_TEXT_BG_PADDING,
          })
        : { top: 0, right: 0, bottom: 0, left: 0 };

      // EWW-470: prefer the TIGHT painted-ink box over Konva's metric box.
      //
      // `containerWidth`/`containerHight` are the metric box, which reserves the font's whole
      // ascender/descender band. Mobile sizes its label straight from the `content_*_ratio` we
      // save here (`FlexibleFrameUtils.getResponsiveWatermarkSize`), paints its background over
      // that whole label and vertically centres the text inside it -- so saving the metric box
      // for an all-caps string handed mobile a box much taller than the glyphs, and the unused
      // band showed up as background above and below the text. Web never showed it because web
      // sizes its own box to the text.
      //
      // `inkContentX`/`inkContentY` (measured in the text node's own space, see
      // `measureTextInkBox`) say where that ink starts inside the metric box, so switching the
      // geometry to the ink also has to shift the origin by the same amount or the watermark
      // would move. Everything below -- bbox, edge ratios, bounding ratios, content ratios --
      // then derives from ONE box, which also keeps `bounding == content` at rotation 0, the
      // same invariant mobile's own encode has (it passes `contentSize: wmLabel.bounds.size`).
      //
      // Falls back to the metric box, unchanged, whenever ink wasn't measurable (older browsers,
      // or a template saved before this shipped).
      const metricTextW = Math.max(0.0001, Number(data.containerWidth) || 0.0001);
      const metricTextH = Math.max(0.0001, Number(data.containerHight) || 0.0001);
      const inkW = Number(data.inkContentWidth) || 0;
      const inkH = Number(data.inkContentHeight) || 0;
      const useInk = inkW > 0 && inkH > 0;
      const unrotTextW = useInk ? inkW : metricTextW;
      const unrotTextH = useInk ? inkH : metricTextH;
      // Offset of the ink inside the metric box; zero when falling back to the metric box.
      const inkOffsetX = useInk ? Number(data.inkContentX) || 0 : 0;
      const inkOffsetY = useInk ? Number(data.inkContentY) || 0 : 0;
      const unrotW = unrotTextW + _insets.left + _insets.right;
      const unrotH = unrotTextH + _insets.top + _insets.bottom;
      const rot = Number(data.deg ?? data.rotation ?? 0) || 0;

      // Admin-style: store flexible_frame based on the ROTATED bbox (axis-aligned).
      // Web text now rotates around CENTER, so rotated bbox stays centered on text center.
      const bbox = this.getBboxSizeFromUnrotated(unrotW, unrotH, rot);
      
      let bboxTopLeftImageX = 0;
      let bboxTopLeftImageY = 0;

      const alignEnable = String(data.alignEnable ?? "");
      if (alignEnable && alignEnable !== "None" && !data.flexible_frame) {
        let topLeftX = imageOffsetX;
        let topLeftY = imageOffsetY;
        const imgW = imageWidth;
        const imgH = imageHeight;

        switch (alignEnable) {
          case "TopLeft":
            topLeftX = imageOffsetX;
            topLeftY = imageOffsetY;
            break;
          case "TopRight":
            topLeftX = imageOffsetX + imgW - bbox.width;
            topLeftY = imageOffsetY;
            break;
          case "BottomLeft":
            topLeftX = imageOffsetX;
            topLeftY = imageOffsetY + imgH - bbox.height;
            break;
          case "BottomRight":
            topLeftX = imageOffsetX + imgW - bbox.width;
            topLeftY = imageOffsetY + imgH - bbox.height;
            break;
          case "Center":
            topLeftX = imageOffsetX + (imgW - bbox.width) / 2;
            topLeftY = imageOffsetY + (imgH - bbox.height) / 2;
            break;
          case "TopCenter":
            topLeftX = imageOffsetX + (imgW - bbox.width) / 2;
            topLeftY = imageOffsetY;
            break;
          case "BottomCenter":
            topLeftX = imageOffsetX + (imgW - bbox.width) / 2;
            topLeftY = imageOffsetY + imgH - bbox.height;
            break;
          case "MidLeft":
            topLeftX = imageOffsetX;
            topLeftY = imageOffsetY + (imgH - bbox.height) / 2;
            break;
          case "MidRight":
            topLeftX = imageOffsetX + imgW - bbox.width;
            topLeftY = imageOffsetY + (imgH - bbox.height) / 2;
            break;
        }
        bboxTopLeftImageX = topLeftX - imageOffsetX;
        bboxTopLeftImageY = topLeftY - imageOffsetY;
      } else {
        const topLeftPx = this.percent_To_pixel(
          Number(data.x) || 0,
          Number(data.y) || 0,
          canvasSize,
          canvasSize
        );
        // EWW-470: `data.x`/`data.y` is the TEXT NODE's top-left (the metric box). When the
        // geometry above switched to the tight ink box, its origin has to move with it by the
        // ink's own offset inside that metric box -- otherwise the saved frame would keep the
        // metric box's origin while carrying the ink's size, and the watermark would shift by
        // the unused ascender band. Both offsets are 0 on the metric-box fallback path.
        const visUnrotX = topLeftPx.x + inkOffsetX - _insets.left;
        const visUnrotY = topLeftPx.y + inkOffsetY - _insets.top;
        const centerX = visUnrotX + unrotW / 2;
        const centerY = visUnrotY + unrotH / 2;
        const bboxTopLeftCanvasX = centerX - bbox.width / 2;
        const bboxTopLeftCanvasY = centerY - bbox.height / 2;
        bboxTopLeftImageX = bboxTopLeftCanvasX - imageOffsetX;
        bboxTopLeftImageY = bboxTopLeftCanvasY - imageOffsetY;
      }

      // EWW-437: Stable-Axis Responsive Scaling -- additive fields alongside the legacy
      // scale_ratio/aspect_ratio (which derive size from min(imageWidth, imageHeight), the
      // root cause of watermarks resizing wrong when template aspect ratio changes). `bbox`/
      // `unrotW`/`unrotH` are already computed above; reused here, not recomputed.
      //
      // Content and bounding ratios are each proportions of their OWN matching canvas dimension
      // (see the image branch above for the full reasoning).
      const flexible_frame = {
        ...this.getFlexibleEdgedFrameValue(
          {
            x: bboxTopLeftImageX,
            y: bboxTopLeftImageY,
            width: bbox.width,
            height: bbox.height,
          },
          { width: imageWidth, height: imageHeight }
        ),
        bounding_width_ratio: bbox.width / imageWidth,
        bounding_height_ratio: bbox.height / imageHeight,
        content_width_ratio: unrotTextW / imageWidth,
        content_height_ratio: unrotTextH / imageHeight,
        content_aspect_ratio: unrotTextW / Math.max(0.0001, unrotTextH),
        size_axis: unrotTextW >= unrotTextH ? (0 as const) : (1 as const),
      };

      // Admin: width = ios_width, height = ios_height (unrotated dimensions)
      const refText = Math.min(imageWidth, imageHeight);
      const ios_width = unrotTextW / Math.max(0.0001, refText);
      const ios_height = unrotTextH / Math.max(0.0001, unrotTextW);

      const shadow =
        data.enableShadowColor || data.shadowColorEnable
          ? data.shadowColor
          : "#00000000";

      dataReturn = {
        date: formatDateDdMmYyyy(),
        id: data.id,
        type: "text",
        tag,
        opacity: data.opacity,
        rotation: rot,
        flexible_frame,
        x: flexible_frame.center_x_ratio,
        y: flexible_frame.center_y_ratio,
        width: ios_width,
        height: ios_height,
        name: "text",
        text: data.src,
        text_color: data.textColor,
        text_background_color: data.textBackgroundColor || "",
        shadow_color: shadow || "#00000000",
        font: this.normalizeTemplateFontForSave(data.font, data.font_id),
        font_id: data.font_id || undefined,
        text_alignment: data.textAlign || "left",
      };
    }

    return dataReturn;
  },
  dataWrapperDecrypt(data: Record<string, any>, platform: string, canvasWidth: number): Record<string, any> {
    // Apply
    let dataReturn: Record<string, any> = {
      ...data,
      type: data.type,
      property: data.sub_type || data.property || (data.type === "text" ? "text" : "image"),
      id: data.id,
      deg: data.rotation,
      width: Number(data.width) * canvasWidth,
      height: Number(data.height) * (Number(data.width) * canvasWidth),
      // Store original width ratio (0.25 = 25% of image width) for responsive scaling
      widthRatio: data.width,
      ScaleApi: true,
      scaleX: data.scaleX || 0,
      scaleY: data.scaleY || 0,
      platform,
      opacity: data.opacity,
      // Store flexible_frame if available (for mobile-style positioning)
      flexible_frame: data.flexible_frame || null,
      ...(data?.flexible_frame &&
      typeof data.height === "number" &&
      Number.isFinite(Number(data.height)) &&
      Number(data.height) > 0
        ? { unrotatedAspectWOverH: 1 / Number(data.height) }
        : {}),
    };

    // For admin-style flexible templates: x/y are center ratios, width/height are ios_width/ios_height.
    // Use flexible_frame content box for top-left (not data.width/height which are scale/aspect).
    const size = (() => {
      try {
        if (data?.flexible_frame && typeof data.x === "number" && typeof data.y === "number") {
          const ff = data.flexible_frame as { left_ratio?: number; right_ratio?: number; top_ratio?: number; bottom_ratio?: number };
          const widthRatio = Math.max(0, 1 - (Number(ff?.left_ratio) || 0) - (Number(ff?.right_ratio) || 0));
          const heightRatio = Math.max(0, 1 - (Number(ff?.top_ratio) || 0) - (Number(ff?.bottom_ratio) || 0));
          const left = Number(data.x) - widthRatio / 2;
          const top = Number(data.y) - heightRatio / 2;
          return { x: left, y: top };
        }
        return this.resetOffset(data.x, data.y, data.width, data.height);
      } catch {
        return { x: 0, y: 0 };
      }
    })();

    if (data.type === "image" || data.type === "signature") {
      // If flexible_frame exists, we'll calculate position later in LogoImage component
      // For now, store the flexible_frame and use legacy coordinates as fallback
      const finalX = Number(data.x) * 100;
      const finalY = Number(data.y) * 100;
      dataReturn = {
        ...dataReturn,
        //   image keys
        name: data.name, /// name format wm_image_unixtimestamp
        size: data.originalSize || data.src?.size, /// watermark size for storage in bytes
        x: finalX,
        y: finalY,
      };
    }
    if (data.type === "text") {
      const finalX = Number(size.x) * 100;
      const finalY = Number(size.y) * 100;

      dataReturn = {
        ...dataReturn,
        //   label keys
        src: data.text, /// text string
        textColor: data.text_color, /// text color should be in HEX with #
        textBackgroundColor: data.text_background_color || "",
        font: this.normalizeTemplateFontForRender(data.font, data.font_id), /// text font family name
        font_id: (data.font_id && uploadedFontManager.findFontById(data.font_id)) ? data.font_id : "",
        shadowColor: data.shadow_color ? data.shadow_color : "#fff", /// shadow color should be in HEX with # -- if this key is empty then shadow is not enable
        shadowColorEnable: data.shadow_color ? true : false,
        textAlign: data.text_alignment, /// text inner aligment left, right , center
        enableShadowColor: data.shadow_color ? true : false,
        sizeOfContainer: data.sizeOfContainer,
        containerHight: data.containerHight,
        containerWidth: data.containerWidth,
        // When coming from admin-style payloads, `scaleFactor` may be absent.
        // Prefer flexible_frame.scale_ratio when available; else data.width (ios_width or legacy widthRatio).
        // Clamp to minimum 0.05: corrupted values (e.g. flexible_frame scale_ratio ~1e-7) would make text tiny.
        scaleFactor: Math.max(0.05, Number(data.scaleFactor) || Number(data?.flexible_frame?.scale_ratio) || Number(data.width) || 0.2),
        multiLine: data.multiLine,
        x: finalX,
        y: finalY,
      };
    }
    return dataReturn;
  },
  // Helper function to pad single-digit numbers with a leading zero
  unixTimestampToDateTime(unixTimestamp: number): string {
    function pad(number) {
      return number < 10 ? "0" + number : number;
    }
    // Multiply by 1000 to convert from seconds to milliseconds
    const milliseconds = unixTimestamp * 1000;

    // Create a new Date object using the milliseconds
    const dateObject = new Date(milliseconds);

    // Extract the various components of the date
    const year = dateObject.getFullYear();
    const month = dateObject.getMonth() + 1; // Months are zero-based
    const day = dateObject.getDate();
    const hours = dateObject.getHours();
    const minutes = dateObject.getMinutes();
    const seconds = dateObject.getSeconds();

    // Format the date and time as a string
    const formattedDateTime = `${year}-${pad(month)}-${pad(day)} ${pad(
      hours
    )}:${pad(minutes)}:${pad(seconds)}`;

    return formattedDateTime;
  },
  /** Pixel size of a data URL / image src (matches what resizeFile will decode). */
  getImageDimensionsFromDataUrl(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => reject(new Error("Failed to decode image dimensions"));
      img.src = dataUrl;
    });
  },
  /**
   * Web port of the iOS app's `getScreenAdjustedImageRect`: contain-fits an image into the
   * screen bounds (or half the screen bounds when `isCanvasImage` is false, matching the
   * iOS thumbnail case) without upscaling past the image's own size. Used to size template
   * thumbnails from actual screen resolution instead of a small hardcoded value (previously
   * a flat 100x100, which looked blurry/unclear once displayed larger than that).
   */
  getScreenAdjustedImageSize(
    imageWidth: number,
    imageHeight: number,
    isCanvasImage: boolean
  ): { width: number; height: number } {
    let screenWidth = window.screen.width;
    let screenHeight = window.screen.height;
    if (!isCanvasImage) {
      screenWidth /= 2;
      screenHeight /= 2;
    }
    const widthFactor = screenWidth / imageWidth;
    const heightFactor = screenHeight / imageHeight;
    // Match the iOS source exactly (always scales to fit, no upscale guard) -- real photos
    // are essentially always larger than half a screen, so this is moot in practice, but
    // keeping it a faithful port rather than adding behaviour iOS doesn't have.
    const factor = Math.min(widthFactor, heightFactor);
    return {
      width: Math.round(imageWidth * factor),
      height: Math.round(imageHeight * factor),
    };
  },
  /** Tiled template save: intrinsic px size when Redux `image` is missing or not decoded. */
  async getPixelSizeFromBlobOrDataUrl(src: string | Blob | File): Promise<{ w: number; h: number } | null> {
    try {
      if (typeof src === "string" && src.startsWith("data:")) {
        const d = await this.getImageDimensionsFromDataUrl(src);
        return d.width > 0 && d.height > 0 ? { w: d.width, h: d.height } : null;
      }
      if (src instanceof Blob && typeof createImageBitmap === "function") {
        const bmp = await createImageBitmap(src);
        const w = bmp.width;
        const h = bmp.height;
        bmp.close?.();
        return w > 0 && h > 0 ? { w, h } : null;
      }
    } catch {
      // ignore
    }
    return null;
  },
  resizeFile(file: string, width: number, height: number, Format: string, Quality: number): Promise<string> {
    // 1600px, 1024px, 960px
    const pica = new Pica();
    const img = new window.Image();
    const canvas = document.createElement("canvas");

    const safeWidth = Math.max(1, Number(width) || 1);
    const safeHeight = Math.max(1, Number(height) || 1);

    return new Promise((resolve) => {
      // On load error or resize failure: fall back to original (e.g. template thumb)
      img.onerror = () => resolve(file);
      img.onload = () => {
        if (!file || !img.naturalWidth || !img.naturalHeight) {
          resolve(file);
          return;
        }
        canvas.width = safeWidth;
        canvas.height = safeHeight;

        pica
          .resize(img, canvas, {
            unsharpAmount: 80,
            unsharpRadius: 0.6,
            unsharpThreshold: 2,
            transferable: true,
          })
          .then((result) => {
            const isPng = String(Format).toLowerCase() === "png";
            if (isPng) {
              // PNG encoder ignores quality in canvas APIs; omit second arg.
              resolve(result.toDataURL("image/png"));
              return;
            }
            // Slider stores 10-100; canvas JPEG expects 0..1.
            const q = Number(Quality);
            const normalizedQuality = Number.isFinite(q)
              ? Math.max(0, Math.min(1, q > 1 ? q / 100 : q))
              : 0.9;
            resolve(result.toDataURL("image/jpeg", normalizedQuality));
          })
          .catch(() => resolve(file));
      };

      img.src = file;
    });
  },
  // Is this watermark in the middle column, sitting exactly at 50% width? Unused today.
  isHorizontalCentered(frame: { region: number; center_x_ratio: number }): boolean {
    const col = frame.region % 3;

    if (col != 1) return false;

    const truncated = Number(frame.center_x_ratio).toFixed(2);

    return truncated == "0.5";
  },
  // Same check, middle row / 50% height. Unused today.
  isVerticalCentered(frame: { region: number; center_y_ratio: number }): boolean {
    const row = frame.region / 3;

    if (row != 1) return false;

    const truncated = Number(frame.center_y_ratio).toFixed(2);

    return truncated == "0.5";
  },
  // True only if centered on both axes.
  isFrameCentered(frame: { region: number; center_x_ratio: number; center_y_ratio: number }): boolean {
    return this.isHorizontalCentered(frame) && this.isVerticalCentered(frame);
  },
};

export default helper_functions;
